import { useCallback } from "react";
import { useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { Investor } from "../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../context/useClientContext";
import InvestorDetailsPage from "./InvestorDetailsPage";

const FundStructureInvestorPage = () => {
  usePageTitle("Investor");

  const { accessCategories } = useClientContext();

  const { id } = useParams();

  const getInvestorDetails = useCallback(() => adminApi.getInvestorDetails(defined(id)), [id]);
  const [investor, fetchInvestorError, { setData: setInvestor }] = useFetch(getInvestorDetails);

  const getObjectDefinition = useCallback(() => adminApi.getObjectDefinition("Investor"), []);
  const [objectDefinitionWithPermissions, fetchDefinitionError] = useFetch(getObjectDefinition);

  const handleInvestorDetailsChange = useCallback(
    (details: Partial<Investor>) => {
      setInvestor((prev) => (prev ? { ...prev, ...details } : undefined));
    },
    [setInvestor]
  );

  if (fetchInvestorError || fetchDefinitionError) {
    logError(fetchInvestorError || fetchDefinitionError, "[FundStructureInvestorPage]");
    return <DataLoadingFailed title="Loading investor details failed" />;
  }

  if (!investor || !objectDefinitionWithPermissions) {
    return <InlineLoader />;
  }

  return (
    <InvestorDetailsPage
      investor={investor}
      categories={accessCategories}
      objectDefinition={objectDefinitionWithPermissions.objectClassDefinition}
      objectPermissions={objectDefinitionWithPermissions.objectPermissions}
      onInvestorDetailsChange={handleInvestorDetailsChange}
    />
  );
};

export default FundStructureInvestorPage;
